import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { Mail, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';

export default function NewsletterSection(): React.JSX.Element {
  const reducedMotion = useReducedMotion();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error');
      return;
    }
    // Mock submission — replace with real API call
    setStatus('success');
    setEmail('');
    setTimeout(() => setStatus('idle'), 4000);
  };

  return (
    <section className="py-20 px-4 bg-white border-y border-slate-100">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-3xl bg-slate-900 p-8 sm:p-12"
        >
          {/* Decorative gradient */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-amber-500/10 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-sky-500/10 rounded-full blur-[80px] pointer-events-none" />

          <div className="relative z-10 text-center">
            <div className="w-12 h-12 mx-auto mb-6 rounded-xl bg-amber-500/15 flex items-center justify-center">
              <Mail className="w-6 h-6 text-amber-400" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Stay Ahead of the Curve
            </h2>
            <p className="text-slate-400 max-w-lg mx-auto mb-8">
              Get weekly AI-in-Finance insights, free workshop invites, and early access to new cohorts. No spam.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
              <label htmlFor="newsletter-email" className="sr-only">
                Email address
              </label>
              <input
                id="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status === 'error') setStatus('idle');
                }}
                placeholder="you@company.com"
                className="w-full sm:flex-1 px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                aria-invalid={status === 'error'}
                aria-describedby={status === 'error' ? 'newsletter-error' : undefined}
              />
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-3 bg-amber-500 text-white rounded-xl font-bold text-sm hover:bg-amber-400 transition-colors shadow-lg shadow-amber-500/25 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                Subscribe <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            {status === 'success' && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 flex items-center justify-center gap-2 text-sm text-emerald-400"
              >
                <CheckCircle className="w-4 h-4" /> You're on the list! Check your inbox.
              </motion.p>
            )}
            {status === 'error' && (
              <motion.p
                id="newsletter-error"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 flex items-center justify-center gap-2 text-sm text-rose-400"
                role="alert"
              >
                <AlertCircle className="w-4 h-4" /> Please enter a valid email address.
              </motion.p>
            )}

            <p className="mt-4 text-xs text-slate-500">
              Join 5,000+ finance professionals. Unsubscribe anytime.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
